<template>
  <div class="instances-page">
    <div class="instances-header">
      <div class="header-content">
        <h1>Instâncias do WhatsApp</h1>
        <Button 
          label="Nova Instância" 
          icon="pi pi-plus" 
          class="p-button-primary" 
          @click="openNewInstanceDialog" 
        />
      </div>
    </div>
    
    <div class="instances-container">
      <div v-if="loading" class="loading-instances">
        <i class="pi pi-spin pi-spinner" style="font-size: 3rem"></i>
        <p>Carregando instâncias...</p>
      </div>
      
      <div v-else-if="instances.length === 0" class="no-instances">
        <i class="pi pi-whatsapp" style="font-size: 4rem; color: var(--green-500)"></i>
        <h2>Nenhuma instância encontrada</h2>
        <p>Crie uma nova instância do WhatsApp para começar a enviar mensagens</p>
        <Button 
          label="Criar Instância" 
          icon="pi pi-plus" 
          class="p-button-primary" 
          @click="openNewInstanceDialog" 
        />
      </div>
      
      <div v-else class="instances-grid">
        <InstanceCard 
          v-for="instance in instances" 
          :key="instance.id" 
          :instance="instance"
          :qrcode="instance.id === connectingInstanceId ? qrcode : null"
          @connect="connectInstance"
          @logout="logoutInstance"
          @delete="confirmDeleteInstance"
          @settings="openInstanceSettings"
        />
      </div>
    </div>
    
    <Dialog 
      v-model:visible="instanceFormVisible" 
      :header="isEditing ? 'Editar Instância' : 'Nova Instância'" 
      :modal="true" 
      :closable="true"
      :style="{width: '500px'}"
    >
      <InstanceForm 
        :initial-instance="currentInstance" 
        :loading="formLoading"
        @submit="saveInstance"
        @cancel="instanceFormVisible = false"
      />
    </Dialog>
    
    <Toast />
    <ConfirmDialog />
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useToast } from 'primevue/usetoast';
import { useConfirm } from 'primevue/useconfirm';
import InstanceCard from '../components/InstanceCard.vue';
import InstanceForm from '../components/InstanceForm.vue';

export default {
  name: 'InstancesPage',
  components: {
    InstanceCard,
    InstanceForm
  },
  setup() {
    const store = useStore();
    const toast = useToast();
    const confirm = useConfirm();
    
    const loading = computed(() => store.getters['whatsapp/isLoading']);
    const instances = computed(() => store.getters['whatsapp/allInstances']);
    const instanceFormVisible = ref(false);
    const isEditing = ref(false);
    const currentInstance = ref(null);
    const formLoading = ref(false);
    const connectingInstanceId = ref(null);
    const qrcode = computed(() => store.getters['whatsapp/qrcode']);
    const connectionState = computed(() => store.getters['whatsapp/connectionState']);
    
    // Monitora mudanças no estado de conexão
    const watchConnectionState = () => {
      if (connectionState.value === 'open' && connectingInstanceId.value) {
        toast.add({
          severity: 'success',
          summary: 'Conectado',
          detail: 'Instância conectada com sucesso!',
          life: 3000
        });
        
        connectingInstanceId.value = null;
        // Recarrega a lista de instâncias para atualizar os status
        loadInstances();
      }
    };
    
    // Carrega a lista de instâncias
    const loadInstances = async () => {
      try {
        await store.dispatch('whatsapp/fetchInstances');
      } catch (error) {
        toast.add({
          severity: 'error',
          summary: 'Erro',
          detail: 'Não foi possível carregar as instâncias',
          life: 3000
        });
      }
    };
    
    // Abre o formulário para criar uma nova instância
    const openNewInstanceDialog = () => {
      isEditing.value = false;
      currentInstance.value = null;
      instanceFormVisible.value = true;
    };
    
    // Abre o formulário para editar uma instância existente
    const openInstanceSettings = async (instanceId) => {
      isEditing.value = true;
      formLoading.value = true;
      
      try {
        // Busca os detalhes da instância
        const instance = await store.dispatch('whatsapp/fetchInstance', { id: instanceId });
        currentInstance.value = instance;
        instanceFormVisible.value = true;
      } catch (error) {
        toast.add({
          severity: 'error',
          summary: 'Erro',
          detail: 'Não foi possível carregar os detalhes da instância',
          life: 3000
        });
      } finally {
        formLoading.value = false;
      }
    };
    
    // Salva uma instância (nova ou atualizada)
    const saveInstance = async (instanceData) => {
      formLoading.value = true;
      
      try {
        if (isEditing.value) {
          // Atualiza uma instância existente
          await store.dispatch('whatsapp/setSettings', {
            instanceId: instanceData.id,
            settings: instanceData.settings
          });
          
          // Atualiza o webhook se for necessário
          if (instanceData.webhook_url || instanceData.webhook_enabled !== undefined) {
            await store.dispatch('whatsapp/setWebhook', {
              instanceId: instanceData.id,
              webhook: {
                url: instanceData.webhook_url,
                enabled: instanceData.webhook_enabled,
                events: instanceData.events
              }
            });
          }
          
          toast.add({
            severity: 'success',
            summary: 'Sucesso',
            detail: 'Instância atualizada com sucesso',
            life: 3000
          });
        } else {
          // Cria uma nova instância
          await store.dispatch('whatsapp/createInstance', {
            instanceName: instanceData.instance_name,
            webhook: {
              url: instanceData.webhook_url,
              enabled: instanceData.webhook_enabled,
              events: instanceData.events
            },
            settings: instanceData.settings
          });
          
          toast.add({
            severity: 'success',
            summary: 'Sucesso',
            detail: 'Instância criada com sucesso',
            life: 3000
          });
        }
        
        // Recarrega a lista de instâncias
        await loadInstances();
        
        // Fecha o formulário
        instanceFormVisible.value = false;
      } catch (error) {
        toast.add({
          severity: 'error',
          summary: 'Erro',
          detail: error.message || 'Ocorreu um erro ao salvar a instância',
          life: 3000
        });
      } finally {
        formLoading.value = false;
      }
    };
    
    // Conecta uma instância
    const connectInstance = async (instanceId) => {
      connectingInstanceId.value = instanceId;
      
      try {
        await store.dispatch('whatsapp/connectInstance', { id: instanceId });
        
        // Inicia a verificação periódica do estado de conexão
        const checkInterval = setInterval(async () => {
          if (connectingInstanceId.value) {
            await store.dispatch('whatsapp/checkConnectionState', { id: instanceId });
            watchConnectionState();
          } else {
            clearInterval(checkInterval);
          }
        }, 3000);
        
        // Limpa o intervalo após 2 minutos para evitar loop infinito
        setTimeout(() => {
          clearInterval(checkInterval);
          if (connectingInstanceId.value === instanceId) {
            connectingInstanceId.value = null;
            toast.add({
              severity: 'warn',
              summary: 'Aviso',
              detail: 'Tempo limite excedido para conexão. Tente novamente.',
              life: 3000
            });
          }
        }, 120000);
      } catch (error) {
        connectingInstanceId.value = null;
        toast.add({
          severity: 'error',
          summary: 'Erro',
          detail: 'Não foi possível iniciar a conexão',
          life: 3000
        });
      }
    };
    
    // Desconecta uma instância
    const logoutInstance = async (instanceId) => {
      try {
        await store.dispatch('whatsapp/logoutInstance', { id: instanceId });
        toast.add({
          severity: 'success',
          summary: 'Sucesso',
          detail: 'Instância desconectada com sucesso',
          life: 3000
        });
        
        // Recarrega a lista de instâncias para atualizar os status
        await loadInstances();
      } catch (error) {
        toast.add({
          severity: 'error',
          summary: 'Erro',
          detail: 'Não foi possível desconectar a instância',
          life: 3000
        });
      }
    };
    
    // Solicita confirmação para excluir uma instância
    const confirmDeleteInstance = (instanceId) => {
      confirm.require({
        message: 'Tem certeza que deseja excluir esta instância?',
        header: 'Confirmação de Exclusão',
        icon: 'pi pi-exclamation-triangle',
        acceptClass: 'p-button-danger',
        accept: () => {
          deleteInstance(instanceId);
        }
      });
    };
    
    // Exclui uma instância
    const deleteInstance = async (instanceId) => {
      try {
        await store.dispatch('whatsapp/deleteInstance', { id: instanceId });
        toast.add({
          severity: 'success',
          summary: 'Sucesso',
          detail: 'Instância excluída com sucesso',
          life: 3000
        });
        
        // Recarrega a lista de instâncias
        await loadInstances();
      } catch (error) {
        toast.add({
          severity: 'error',
          summary: 'Erro',
          detail: 'Não foi possível excluir a instância',
          life: 3000
        });
      }
    };
    
    // Carrega a lista de instâncias ao montar o componente
    onMounted(() => {
      loadInstances();
    });
    
    return {
      loading,
      instances,
      instanceFormVisible,
      isEditing,
      currentInstance,
      formLoading,
      connectingInstanceId,
      qrcode,
      openNewInstanceDialog,
      openInstanceSettings,
      saveInstance,
      connectInstance,
      logoutInstance,
      confirmDeleteInstance
    };
  }
};
</script>

<style scoped>
.instances-page {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.instances-header {
  background-color: var(--paper-deep);
  padding: 1.5rem 2rem;
  border-bottom: 2px solid var(--ink);
  z-index: 1;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
}

.header-content h1 {
  font-size: 1.8rem;
  font-weight: 600;
  color: var(--ink);
  margin: 0;
}

.instances-container {
  flex: 1;
  padding: 2rem;
  background-color: var(--paper);
  overflow-y: auto;
}

.instances-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 1.5rem;
  max-width: 1200px;
  margin: 0 auto;
}

.loading-instances, .no-instances {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  min-height: 60vh;
  color: var(--ink-soft);
}

.no-instances {
  gap: 1rem;
}

.no-instances h2 {
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0.5rem 0;
  color: var(--ink);
}

.no-instances p {
  margin-bottom: 1.5rem;
  font-size: 1.1rem;
}

@media (max-width: 768px) {
  .instances-grid {
    grid-template-columns: 1fr;
  }
  
  .header-content {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
}
</style> 